import type { AdsActivityResponse, AdsActivityStatusVerdict } from "@/lib/adsAssistantClientEngagement";
import { computeStatusVerdict, type StatusVerdict } from "@/lib/adsActivityVerdict";
import { isAdsSalesFocusUser } from "@/lib/adsAssistantPermissions";
import type { KpiTone } from "@/components/common/kpi";

export type AdsHeaderActivityPillDisplay = {
  visible: boolean;
  tone: KpiTone;
  count: number;
  goal: number;
  showGoal: boolean;
  label: string;
  ariaLabel: string;
};

function isKpiTone(v: string): v is KpiTone {
  return v === "amber" || v === "green" || v === "gold" || v === "red" || v === "grey";
}

function verdictToKpiTone(v: AdsActivityStatusVerdict | StatusVerdict | null | undefined): KpiTone {
  const tone = v?.tone;
  if (tone && isKpiTone(tone)) return tone;
  return "grey";
}

export function resolveAdsHeaderActivityPill(
  activity: AdsActivityResponse | null,
  auth: { status: string; hasRole: (...r: string[]) => boolean },
): AdsHeaderActivityPillDisplay | null {
  if (!activity || activity.use_in_org_activity === false) return null;

  const goal = activity.ads_daily_goal ?? 0;
  const salesFocus = isAdsSalesFocusUser(auth);

  if (salesFocus) {
    const myToday = activity.my_posts_today ?? 0;
    if (goal <= 0 && myToday === 0) return null;
    const verdict = activity.status_verdict;
    const tone = verdictToKpiTone(verdict);
    const label = verdict?.label ?? "Org activity";
    return {
      visible: true,
      tone,
      count: myToday,
      goal,
      showGoal: goal > 0,
      label,
      ariaLabel: goal > 0 ? `Ads today: ${myToday} of ${goal}. ${label}.` : `Ads today: ${myToday}. ${label}.`,
    };
  }

  const orgToday = activity.posts_today_count ?? 0;
  if (goal <= 0 && orgToday === 0) return null;

  const cap = activity.daily_max_per_platform_total ?? 0;
  const orgVerdict = computeStatusVerdict({
    todayCount: orgToday,
    goal,
    capTotal: cap,
    redAfterLocalTime: activity.red_cutoff_local_time ?? "15:00",
    nowLocalIso: new Date().toISOString(),
  });

  return {
    visible: true,
    tone: verdictToKpiTone(orgVerdict),
    count: orgToday,
    goal,
    showGoal: goal > 0,
    label: orgVerdict.label,
    ariaLabel: goal > 0 ? `Org ads today: ${orgToday} of ${goal}. ${orgVerdict.label}.` : `Org ads today: ${orgToday}. ${orgVerdict.label}.`,
  };
}
